/**
 * Wraps an async function so only one invocation can run at a time.
 *
 * While an invocation is in progress, any additional calls return the same
 * in-flight promise instead of starting a new execution.
 *
 * This utility is useful when you want to prevent duplicate submissions or
 * repeated button clicks from triggering the same async action multiple times.
 *
 * This composable is intentionally generic and does not track reactive state.
 * It only manages promise-level locking.
 *
 * @typeParam TArgs - The tuple of arguments accepted by the wrapped function.
 * @typeParam TResult - The resolved value returned by the wrapped function.
 *
 * @param fn - The async function to lock.
 * @returns A wrapped function that reuses the active promise while running.
 *
 * @example
 * ```ts
 * const saveOnce = usePromiseLock(async (id: string) => {
 *   await api.save(id)
 *   return true
 * })
 *
 * await saveOnce("123")
 * ```
 *
 * @example
 * If called multiple times before the first invocation completes, all callers
 * receive the same promise:
 *
 * ```ts
 * const locked = usePromiseLock(async () => {
 *   await new Promise(resolve => setTimeout(resolve, 1000))
 *   return "done"
 * })
 *
 * const a = locked()
 * const b = locked()
 *
 * console.log(a === b) // true
 * ```
 */
export function usePromiseLock<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>) {
  /**
   * Stores the currently running promise.
   *
   * When null, no invocation is active and a new one may begin.
   * When non-null, the existing promise is returned to all callers.
   */
  let promise: Promise<TResult> | null = null;

  /**
   * Executes the wrapped async function.
   *
   * If an invocation is already in progress, the existing in-flight promise is
   * returned immediately. Otherwise, a new invocation starts and becomes the
   * active locked promise until it settles.
   *
   * @param args - Arguments forwarded to the wrapped function.
   * @returns The active promise for the current execution.
   */
  return async (...args: TArgs): Promise<TResult> => {
    if (promise) {
      return promise;
    }

    promise = fn(...args).finally(() => {
      promise = null;
    });

    return promise;
  };
};
